import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import { DataTable, DataTableRow } from 'components/dataTable';
import { TableFilters } from 'components/filters/table/tableFilters';
import { TableFiltersProps } from 'components/filters/table/types';
import IconAdd from 'components/icons/add';
import IconRefundBalance from 'components/icons/refundBalance';

import { TablePagination } from 'types/pagination';
import { TableSorting } from 'types/sort';

import { formatCryptoAmountWithSymbolAtEnd } from 'utils/cryptoFormatter';
import { formatFiatCurrencyI18n } from 'utils/i18n';
import { calculateFiatEstimate } from 'utils/refundFx';

import { RefundAccountCell } from './components/cells';
import TopUpModal from './components/topupModal';
import { COLUMNS, LABELS } from './keys';
import { ActionBar, ApproximateAmount, Balance, BalanceAmounts, Container, ErrorText, TablePlaceholder, TopUpButton } from './styles';
import { Filters } from './types';
import { makeFilterSections } from './utils';

interface RefundAccountProps {
  refundAccountTransactions: DataTableRow[];
  refundWalletBalance?: string;
  settlementCurrencyCode: string;
  fiatCurrencyCode?: string;
  displayFxRate?: string | null;
  error?: string;
  isLoading: boolean;
  pagination: TablePagination;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (itemsPerPage: number) => void;
  onSort: (event: MouseEvent, property: string) => void;
  sorting: TableSorting;
  filterProps: TableFiltersProps<Filters>;
}

const RefundAccount = ({
  refundAccountTransactions,
  refundWalletBalance,
  settlementCurrencyCode,
  fiatCurrencyCode,
  displayFxRate,
  error,
  isLoading,
  pagination,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
  sorting,
  filterProps,
}: RefundAccountProps) => {
  const { t, i18n } = useTranslation();
  const sections = makeFilterSections(filterProps.filterSettings);
  const [isTopupModalOpen, setIsTopupModalOpen] = useState(false);
  const fiatEstimate = calculateFiatEstimate(refundWalletBalance, displayFxRate);

  const toggleTopupModal = () => {
    setIsTopupModalOpen(!isTopupModalOpen);
  };

  const getPlaceholder = () => {
    if (error) {
      return (
        <TablePlaceholder>
          <ErrorText>{error}</ErrorText>
        </TablePlaceholder>
      );
    }

    return <TablePlaceholder>{t(LABELS.TABLE_PLACEHOLDER)}</TablePlaceholder>;
  };

  return (
    <Panel label={t(LABELS.PAGE_TITLE)}>
      <Container>
        {refundWalletBalance && (
          <Balance>
            <span>
              <IconRefundBalance />
              {t(LABELS.REFUND_WALLET_BALANCE)}
            </span>
            <BalanceAmounts>
              {formatCryptoAmountWithSymbolAtEnd(refundWalletBalance, settlementCurrencyCode, {
                precision: 2,
                trimTrailingZeros: false,
              })}
              {fiatEstimate !== undefined && fiatCurrencyCode && (
                <ApproximateAmount>≈ {formatFiatCurrencyI18n(i18n.language)(fiatEstimate, fiatCurrencyCode)}</ApproximateAmount>
              )}
            </BalanceAmounts>
          </Balance>
        )}
        <ActionBar>
          <TableFilters {...filterProps} sections={sections} />
          <TopUpButton flat onClick={toggleTopupModal}>
            <IconAdd />
            {t(LABELS.ACTIONS.TOGGLE_TOPUP_MODAL)}
          </TopUpButton>
        </ActionBar>
        <DataTable
          columns={COLUMNS}
          rows={refundAccountTransactions}
          CellComponent={RefundAccountCell}
          pagination={pagination}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onSort={onSort}
          order={sorting.order}
          orderBy={sorting.orderBy}
          loading={isLoading}
          placeholder={getPlaceholder()}
        />
      </Container>
      <TopUpModal isOpen={isTopupModalOpen} toggle={toggleTopupModal} />
    </Panel>
  );
};

export default RefundAccount;
